import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { useCredits } from "@/context/CreditsContext";
import { Users, ListOrdered, PlusCircle, Sparkles, Medal, Check, X, ChevronRight, PartyPopper, Crown } from "lucide-react";

// The 5 premium features we want every new user to taste once.
const FEATURES = [
  { key: "follow", label: "Follow a competitor", desc: "Track a rival's entries & results", icon: Users, to: "/riders" },
  { key: "draw_lookup", label: "Look up a rodeo draw", desc: "See who's up and when", icon: ListOrdered, to: "/table" },
  { key: "prepared_entry", label: "Prep an entry", desc: "Jump to enter a rodeo", icon: PlusCircle, to: "/table" },
  { key: "ai_coach", label: "Get season advice", desc: "Plan your run for the year", icon: Sparkles, to: "/season" },
  { key: "results_audit", label: "Review results", desc: "Dig into placings & payouts", icon: Medal, to: "/results" },
];

export default function SamplerChecklist({ m }) {
  const nav = useNavigate();
  const { freeTrials, credits } = useCredits();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("rp_sampler_dismissed") === "1");

  const done = new Set(freeTrials || []);
  const doneCount = FEATURES.filter((f) => done.has(f.key)).length;
  const complete = doneCount >= FEATURES.length;

  // Fire confetti once, the first time they complete all five.
  useEffect(() => {
    if (complete && localStorage.getItem("rp_sampler_celebrated") !== "1") {
      localStorage.setItem("rp_sampler_celebrated", "1");
      const burst = (opts) => confetti({ particleCount: 70, spread: 75, startVelocity: 45, ticks: 220,
        colors: ["#C8000F", "#E0A526", "#16A34A", "#ffffff"], ...opts });
      burst({ origin: { x: 0.25, y: 0.6 }, angle: 60 });
      burst({ origin: { x: 0.75, y: 0.6 }, angle: 120 });
      setTimeout(() => burst({ origin: { x: 0.5, y: 0.5 } }), 250);
    }
  }, [complete]);

  if (dismissed) return null;
  const left = FEATURES.length - doneCount;

  const card = m ? "bg-[#14202E] border-[#1E3A52]" : "bg-white border-[#E4E7EB] shadow-sm";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const sub = m ? "text-slate-400" : "text-slate-500";
  const rowIdle = m ? "bg-white/5 hover:bg-white/10" : "bg-[#F7F8FA] hover:bg-[#EEF1F4]";

  const close = () => { localStorage.setItem("rp_sampler_dismissed", "1"); setDismissed(true); };

  // Finale: they've seen it all → convert.
  if (complete) {
    return (
      <section className={`rounded-2xl border p-5 sm:p-6 relative text-center overflow-hidden ${card}`} data-testid="sampler-complete">
        <button onClick={close} data-testid="sampler-dismiss" className={`absolute top-3 right-3 p-1 rounded-lg ${sub} hover:opacity-80`}><X className="w-4 h-4" /></button>
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#16A34A]/15 mb-2">
          <PartyPopper className="w-7 h-7 text-[#16A34A]" />
        </div>
        <h2 className={`font-display font-extrabold text-xl ${title}`}>You've seen it all! 🎉</h2>
        <p className={`text-sm mt-1.5 max-w-md mx-auto ${sub}`}>You've tried all 5 premium features. Pick a plan to keep following rivals, pulling draws and prepping entries — no limits.</p>
        <button onClick={() => nav("/choose-plan")} data-testid="sampler-complete-upgrade"
          className="mt-4 inline-flex items-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white font-semibold px-6 py-2.5 rounded-lg">
          <Crown className="w-4 h-4" /> Subscribe to keep going
        </button>
      </section>
    );
  }

  return (
    <section className={`rounded-2xl border p-4 sm:p-5 relative overflow-hidden ${card}`} data-testid="sampler-checklist">
      <button onClick={close} data-testid="sampler-dismiss" className={`absolute top-3 right-3 p-1 rounded-lg ${sub} hover:opacity-80`}><X className="w-4 h-4" /></button>

      <div className="flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-[#C8000F]" />
        <h2 className={`font-display font-extrabold text-lg ${title}`}>Explore Rodeo Pilot</h2>
      </div>
      <p className={`text-sm mt-1 ${sub}`}>
        You've tried <b className={title}>{doneCount} of {FEATURES.length}</b> features — {left} to go. Your first use of each is on us{credits != null ? `, plus you have ${credits} credit${credits === 1 ? "" : "s"} to go deeper.` : "."}
      </p>

      {/* progress bar */}
      <div className={`mt-3 h-1.5 rounded-full overflow-hidden ${m ? "bg-white/10" : "bg-[#EDEFF2]"}`}>
        <div className="h-full bg-[#C8000F] transition-all duration-500" style={{ width: `${(doneCount / FEATURES.length) * 100}%` }} data-testid="sampler-progress" />
      </div>

      <div className="mt-4 space-y-2">
        {FEATURES.map((f) => {
          const isDone = done.has(f.key);
          const Icon = f.icon;
          return (
            <button key={f.key} onClick={() => !isDone && nav(f.to)} disabled={isDone} data-testid={`sampler-item-${f.key}`}
              className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${isDone ? "opacity-60 cursor-default" : rowIdle}`}>
              <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${isDone ? "bg-[#16A34A] text-white" : (m ? "bg-white/10 text-[#C8000F]" : "bg-[#FBE9EA] text-[#C8000F]")}`}>
                {isDone ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </span>
              <span className="flex-1 min-w-0">
                <span className={`block text-sm font-semibold ${isDone ? "line-through " + sub : title}`}>{f.label}</span>
                <span className={`block text-[12px] truncate ${sub}`}>{isDone ? "Done — nice!" : f.desc}</span>
              </span>
              {!isDone && <span className="shrink-0 flex items-center gap-1 text-[12px] font-bold text-[#C8000F]">Try <ChevronRight className="w-4 h-4" /></span>}
            </button>
          );
        })}
      </div>

      {doneCount >= 1 && doneCount < FEATURES.length && (
        <button onClick={() => nav("/choose-plan")} data-testid="sampler-upgrade" className="mt-4 w-full bg-[#C8000F] hover:bg-[#9E0008] text-white font-semibold text-sm py-2.5 rounded-lg">
          Liking it? See plans to unlock unlimited
        </button>
      )}
    </section>
  );
}
